/**
 * What a therapist keeps of what the clinic bills.
 *
 * **One rate is active at a time and the next supersedes it.** Older rates are kept by the API so
 * that a month already worked keeps the rate it was worked under, but nothing ever displays them
 * and no endpoint returns them — so there is no history type here.
 *
 * **Two rates travel together, because a rate takes force on the first of the month after it is
 * set.** For most of any month the number an admin last typed is not yet the number the money is
 * being worked out at. `percent` is the one *in force* — the rate every figure on the clinic
 * dashboard was computed at, which is what lets the two be read against each other — and
 * `scheduled*` is the change waiting behind it.
 */
export interface ITherapistRevenueShare {
    therapistId: number;
    /**
     * The discipline (a catalogue type) this rate is for, or null for the therapist's default.
     * A therapist comes back once per scope they have a rate in, the default first: a sale is
     * paid at its discipline's override where one is in force, else at the default.
     */
    catalogTypeId: number | null;
    /**
     * The rate in force today, or null when none is. Null covers both a share that was cleared and
     * one whose only rate has not started yet. Render it as an absence, never as a zero: a zero
     * says they keep nothing, which is a different arrangement and a real one.
     */
    percent: number | null;
    /** `YYYY-MM-DD`. Null alongside a null percent. */
    effectiveFrom: string | null;
    /**
     * The queued rate, or null. **Null does not mean nothing is queued** — a queued *clearing*
     * has a null percent and a real date, so `scheduledFrom` is the field to test.
     */
    scheduledPercent: number | null;
    /** When the queued change takes force, or null when nothing is queued. Always a first of month. */
    scheduledFrom: string | null;
}

/** One discipline's override in a save. A null percent puts it back on the default. */
export interface DisciplineSharePayload {
    catalogTypeId: number;
    percent: number | null;
}

/**
 * What `PUT /therapists/{id}/revenue-share` takes. No date — the server owns that.
 *
 * A discipline left out of `disciplines` is left alone, and the server writes only what differs
 * from what is in force or queued, so sending the whole editor back is safe.
 */
export interface TherapistRevenueSharePayload {
    therapistId: number;
    /** Null clears the default share. */
    percent: number | null;
    disciplines: DisciplineSharePayload[];
}

/** One discipline's row in the editor; blank means "uses the default". */
export interface DisciplineShareFormValue {
    catalogTypeId: number;
    percent: string;
}

/** Held as strings like every number in this app's form values. */
export interface TherapistShareFormValues {
    percent: string;
    disciplines: DisciplineShareFormValue[];
}

/**
 * One scope of a therapist's share — the default, or one discipline's override — or undefined
 * when the clinic has set none there.
 *
 * A scope absent from the listing and one whose rate was cleared mean the same thing (the whole
 * income for the default, the default for an override), so a caller has one case to render.
 */
export const shareFor = (
    shares: ITherapistRevenueShare[] | undefined,
    therapistId: number,
    catalogTypeId: number | null = null
): ITherapistRevenueShare | undefined =>
    shares?.find(share => share.therapistId === therapistId
        && share.catalogTypeId === catalogTypeId);

/** Every scope a therapist has a rate in, the default included. */
export const sharesOf = (
    shares: ITherapistRevenueShare[] | undefined,
    therapistId: number
): ITherapistRevenueShare[] =>
    shares?.filter(share => share.therapistId === therapistId) ?? [];

/** A therapist's discipline overrides that have a rate in force — what the table counts. */
export const overridesFor = (
    shares: ITherapistRevenueShare[] | undefined,
    therapistId: number
): ITherapistRevenueShare[] =>
    sharesOf(shares, therapistId)
        .filter(share => share.catalogTypeId !== null && share.percent !== null);

/**
 * Whether a change is waiting to take force.
 *
 * Reads the **date**, not the percent. A queued clearing carries a null percent and a real date,
 * and testing the percent would miss exactly that case — which is the one where an admin most
 * needs telling that something is about to change.
 */
export const hasScheduledChange = (share: ITherapistRevenueShare | undefined): boolean =>
    !!share?.scheduledFrom;

/**
 * What the editor opens on: the queued rate if there is one, else what is in force.
 *
 * The queued one, because that is what a second edit is correcting. Opening on the in-force rate
 * would quietly offer to undo a change the admin made ten minutes ago.
 */
export const shareEditValue = (share: ITherapistRevenueShare | undefined): number | null =>
    hasScheduledChange(share) ? share!.scheduledPercent : share?.percent ?? null;
